import { createApp } from 'vue';
import { createPinia } from 'pinia';
import { IonicVue } from '@ionic/vue';
import { Capacitor } from '@capacitor/core';

import App from './App.vue';
import { router } from '@/app/router';
import { setHydrationPromise } from '@/app/router/guards';
import { i18n } from '@/app/i18n';
import { useAuthStore } from '@/stores/auth.store';
import { useCentralAuthStore } from '@/stores/central-auth.store';
import { CONFIG } from '@/app/config/env';
import { initSentry, setSentryUser } from '@/core/observability/sentry';
import { effectiveSubdomain } from '@/core/http/tenantResolver';

import '@/app/theme/index.scss';

/** Register the vite-plugin-pwa service worker. autoUpdate → a new deploy
 *  installs and takes over on the next load; we don't prompt. Failures here
 *  must never block the app, so they're swallowed. */
async function registerServiceWorker(): Promise<void> {
  try {
    const { registerSW } = await import('virtual:pwa-register');
    registerSW({ immediate: true });
  } catch {
    /* SW unsupported (or blocked) — the app still runs, just online-only. */
  }
}

async function bootstrap() {
  const app = createApp(App);
  const pinia = createPinia();

  app.use(pinia);
  app.use(IonicVue, { mode: 'md' });
  app.use(i18n);

  // Sentry must init before the router installs so route-change breadcrumbs
  // are captured. No-op when VITE_SENTRY_DSN is empty (local dev).
  initSentry(app, router);

  // Kick off hydration BEFORE the router installs so guards can await it.
  const auth = useAuthStore();
  const centralAuth = useCentralAuthStore();
  const hydration = (async () => {
    await Promise.all([auth.hydrateFromStorage(), centralAuth.hydrateFromStorage()]);
    await auth.wipeIfSubdomainMismatch();
    // Restore the persisted active company/branch so the first API request
    // after a reload already carries the context headers.
    const { useCompanyStore } = await import('@/stores/company.store');
    await useCompanyStore().hydrate();
  })();
  setHydrationPromise(hydration);

  app.use(router);

  // Await hydration before router.isReady() so the initial navigation guard
  // sees the fully-hydrated auth state.
  await hydration;

  // Attach the current auth identity to Sentry so errors after mount
  // include who was signed in and which tenant they were on.
  if (auth.isAuthenticated && auth.user) {
    setSentryUser({
      id: auth.user.id,
      email: auth.user.email,
      tenant: effectiveSubdomain(),
    });
  } else if (centralAuth.isAuthenticated && centralAuth.admin) {
    setSentryUser({
      id: centralAuth.admin.id,
      email: centralAuth.admin.email,
      tenant: 'central',
    });
  }

  await router.isReady();
  app.mount('#app');

  // Native splash: hide as soon as the app has mounted so there's no fixed
  // blank delay (launchAutoHide is the safety net). No-op on web.
  if (Capacitor.isNativePlatform()) {
    void import('@capacitor/splash-screen').then(({ SplashScreen }) => SplashScreen.hide());
  }

  // Register the PWA service worker on the WEB only, so a cold reload with no
  // network still serves the precached app shell. Native Capacitor bundles its
  // own assets and its webview must not run our SW.
  if (!Capacitor.isNativePlatform()) {
    void registerServiceWorker();
  }

  // Kick off the offline outbox pump (§14).
  if (CONFIG.ENABLE_OUTBOX && auth.isAuthenticated) {
    const { startOutboxPump } = await import('@/shared/composables/useOutbox');
    void startOutboxPump();
  }

  // Prime the offline read mirrors (customers/products/stock) now and on every
  // reconnect, so the app is browsable offline without first visiting each list.
  // Skipped for a dental-only doctor: they have no offline POS/catalog use, so
  // the mirror deltas would only fire (silently denied) 403s on every login.
  if (auth.isAuthenticated) {
    const { usePermissionStore } = await import('@/stores/permission.store');
    if (!usePermissionStore().isDoctorOnly) {
      const { startSyncEngine } = await import('@/core/offline/syncEngine');
      void startSyncEngine();
      // Drain the generic offline write queue (e.g. customers created offline).
      const { startCommandPump } = await import('@/core/offline/commandOutbox');
      void startCommandPump();
    }
  }
}

void bootstrap();
